import React, { useEffect, useState } from "react";
import {
  Typography,
  Avatar,
  Paper,
  Box,
  Divider,
  Tabs,
  Tab,
  TextField,
  Button,
  Link,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Menu,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { binanceApi } from "../../services/binance.api.service";
import { useApp } from "../../context/AppContext";
import { useLanguage } from "../../context/LanguageContext";
import { useTotalBalance } from "../../hooks/useTotalBalance";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";

import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import "../../assets/style/Wallets.css";

import BtcIcons from "../../assets/img/icons/btc.png";
import EtheriumIcon from "../../assets/img/icons/eth.png";
import TetherUSDTIcon from "../../assets/img/icons/usdt.png";
import BNBIcon from "../../assets/img/icons/bnb.png";
import SolanaIcon from "../../assets/img/icons/sol.png";
import XRPIcon from "../../assets/img/icons/xrp.png";
import CardanoIcon from "../../assets/img/icons/ada.png";
import DogecoinIcon from "../../assets/img/icons/doge.png";
import LitecoinIcon from "../../assets/img/icons/ltc.png";
import MoneroIcon from "../../assets/img/icons/xmr.png";
import PolygonIcon from "../../assets/img/icons/matic.png";
import TRONIcon from "../../assets/img/icons/trx.png";
import USDIcon from "../../assets/img/icons/usd.png";
import EURIcon from "../../assets/img/icons/eur.png";

const CurrencyIcon = ({ img }) => (
  <div className="currency-icon-placeholder">
    <img src={img} alt="" />
  </div>
);

const currencies = [
  { symbol: "BTC", name: "Bitcoin", icon: <CurrencyIcon img={BtcIcons} /> },
  {
    symbol: "ETH",
    name: "Ethereum",
    icon: <CurrencyIcon img={EtheriumIcon} />,
  },
  {
    symbol: "USDT",
    name: "Tether USDT",
    icon: <CurrencyIcon img={TetherUSDTIcon} />,
  },
  { symbol: "BNB", name: "BNB", icon: <CurrencyIcon img={BNBIcon} /> },
  { symbol: "SOL", name: "Solana", icon: <CurrencyIcon img={SolanaIcon} /> },
  { symbol: "XRP", name: "XRP", icon: <CurrencyIcon img={XRPIcon} /> },
  {
    symbol: "ADA",
    name: "Cardano",
    icon: <CurrencyIcon img={CardanoIcon} />,
  },
  {
    symbol: "DOGE",
    name: "Dogecoin",
    icon: <CurrencyIcon img={DogecoinIcon} />,
  },
  {
    symbol: "LTC",
    name: "Litecoin",
    icon: <CurrencyIcon img={LitecoinIcon} />,
  },
  { symbol: "XMR", name: "Monero", icon: <CurrencyIcon img={MoneroIcon} /> },
  {
    symbol: "MATIC",
    name: "Polygon",
    icon: <CurrencyIcon img={PolygonIcon} />,
  },
  { symbol: "TRX", name: "TRON", icon: <CurrencyIcon img={TRONIcon} /> },
  { symbol: "USD", name: "USD", icon: <CurrencyIcon img={USDIcon} /> },
  { symbol: "EUR", name: "EUR", icon: <CurrencyIcon img={EURIcon} /> },
];
const norm = (v) => (v ?? "").toString().trim().toUpperCase();

const buildDisplayWallet = (symbol, wallets) => {
  const currency = currencies.find((c) => norm(c.symbol) === norm(symbol));
  if (!currency) return null;

  const realWallet = (wallets || []).find((w) => {
    const assetName = norm(w.asset_name);
    return assetName === norm(currency.name) || assetName === norm(currency.symbol);
  });

  return {
    ...currency,
    balance: realWallet?.balance ?? 0,
    total_balance: realWallet?.total_balance ?? realWallet?.balance ?? 0,
    address: realWallet?.address ?? null,
    id: realWallet?.id ?? null,
    asset_id: realWallet?.asset_id ?? null,
    color: currency.color || "#ff9800",
  };
};

function WalletListItem({ name, balance, icon }) {
  const formattedBalance = parseFloat(balance || 0).toLocaleString('en-US', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8
  });

  return (
    <div className="wallet-list-item">
      <div className="wallet-info">
        <div className="wallet-icon">{icon}</div>
        <div>
          <Typography className="wallet-name">{name} Wallet</Typography>
        </div>
      </div>
      <div className="wallet-balance">
        <Typography className="wallet-crypto-value">{formattedBalance}</Typography>
      </div>
    </div>
  );
}

function YourAssetsCard() {
  const { wallets, fetchWallets, loading } = useApp();
  const { t } = useLanguage();
  const {
    totalBalanceUSD,
    currentBalanceUSD,
    loading: balanceLoading
  } = useTotalBalance(wallets);

  useEffect(() => {
    // Делаем запрос при каждом переходе на страницу
    fetchWallets();
  }, []); // Запускаем только один раз при монтировании

  return (
    <Paper elevation={0} className="card your-assets-card">
      <Typography variant="h6" className="card-title">
        {t('yourAssets')}
      </Typography>
      <Box className="total-assets-section">

        <div className="assets-grid">

          <div className="asset-box">
            <Typography className="asset-label">
              💰 Total Assets
            </Typography>

            <Typography className="asset-value total">
              {balanceLoading
                ? t('loading')
                : `${totalBalanceUSD.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })} $`}
            </Typography>
          </div>

          <div className="asset-box">
            <Typography className="asset-label">
              ⚡ Available
            </Typography>

            <Typography className="asset-value current">
              {balanceLoading
                ? t('loading')
                : `${currentBalanceUSD.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })} $`}
            </Typography>
          </div>

        </div>

      </Box>
      <Divider className="card-divider" />
      <div className="wallet-list">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Typography variant="h6">{t('loading')}</Typography>
          </div>
        ) : wallets && wallets.length > 0 ? (
          wallets.map((wallet) => {
            const currencyData = currencies.find( (c) => c.name === wallet.asset_name );
            const walletIcon = currencyData ? currencyData.icon : null;

            return (
              <WalletListItem
                key={wallet.asset_name}
                name={wallet.asset_name}
                balance={wallet.balance}
                icon={walletIcon}
              />
            );
          })
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Typography variant="h6">{t('walletNotFound')}</Typography>
          </div>
        )}
      </div>
    </Paper>
  );
}

const CurrencyDisplay = ({ wallet }) => {
  const formattedBalance = parseFloat(wallet.balance || 0).toLocaleString('en-US', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8
  });

  /*console.log('wallet:', {
    formattedBalance,
    wallet
  });*/

  return (
    <div className="wallet-select-item">
      <Avatar
        className="wallet-select-avatar"
        style={{ backgroundColor: wallet.color }}
      >
        {wallet.icon}
      </Avatar>
      <div className="wallet-select-details">
        <Typography className="wallet-select-name">{wallet.name}</Typography>
        <Typography className="wallet-select-balance">
          {formattedBalance}
        </Typography>
      </div>
    </div>
  );
};
// ! --- Основной компонент ---

function SendPanel() {
  const navigate = useNavigate();

  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState(
    currencies[0]?.symbol || ""
  );
  const [amount, setAmount] = useState("");
  const [sendAddress, setSendAddress] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [securityStatus, setSecurityStatus] = useState({
    loading: true,
    google_authenticator_enabled: false,
    email_2fa_enabled: false,
  });
  const [securityStatusError, setSecurityStatusError] = useState("");
  const [confirmationMethod, setConfirmationMethod] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [emailCodeLoading, setEmailCodeLoading] = useState(false);
  const [emailCodeMessage, setEmailCodeMessage] = useState("");
  const { wallets } = useApp();
  const { t } = useLanguage();

  // Проверяем, есть ли кошельки
  useEffect(() => {
    if (wallets.length === 0) {
      setErrors({ wallet: "No wallets available. Please contact support." });
    } else {
      // Очищаем ошибку, если кошельки появились
      if (errors.wallet === "No wallets available. Please contact support.") {
        setErrors(prev => ({ ...prev, wallet: null }));
      }
    }
  }, [wallets]);

  useEffect(() => {
    let isActive = true;

    const loadSecurityStatus = async () => {
      setSecurityStatusError("");

      try {
        const [authenticatorResult, emailResult] = await Promise.allSettled([
          api.get("/authenticator-app/status"),
          api.get("/email-authentication/status"),
        ]);

        if (!isActive) return;

        const nextStatus = {
          loading: false,
          google_authenticator_enabled:
            authenticatorResult.status === "fulfilled"
              ? Boolean(authenticatorResult.value.data.google_authenticator_enabled)
              : false,
          email_2fa_enabled:
            emailResult.status === "fulfilled"
              ? Boolean(emailResult.value.data.email_2fa_enabled)
              : false,
        };

        setSecurityStatus(nextStatus);

        if (authenticatorResult.status === "rejected" && emailResult.status === "rejected") {
          setSecurityStatusError("Unable to load withdrawal confirmation settings.");
        }

        setConfirmationMethod((current) => {
          if (current === "authenticator" && nextStatus.google_authenticator_enabled) return current;
          if (current === "email" && nextStatus.email_2fa_enabled) return current;
          if (nextStatus.google_authenticator_enabled) return "authenticator";
          if (nextStatus.email_2fa_enabled) return "email";
          return "";
        });
      } catch (error) {
        if (!isActive) return;

        setSecurityStatus({
          loading: false,
          google_authenticator_enabled: false,
          email_2fa_enabled: false,
        });
        setSecurityStatusError("Unable to load withdrawal confirmation settings.");
      }
    };

    loadSecurityStatus();

    return () => {
      isActive = false;
    };
  }, []);

  const sendAddressInput = (event) => {
    const value = event.target.value;
    setSendAddress(value);
    
    // Очищаем ошибку адреса при вводе
    if (errors.address) {
      setErrors(prev => ({ ...prev, address: null }));
    }
    
    // Валидация адреса в реальном времени
    if (value.trim()) {
      const addressLength = value.trim().length;
      let minLength = 10; // Минимальная длина по умолчанию
      
      // Устанавливаем минимальную длину в зависимости от валюты
      switch (selectedCurrencySymbol) {
        case 'BTC':
          minLength = 26;
          break;
        case 'ETH':
          minLength = 42;
          break;
        case 'TRX':
          minLength = 34;
          break;
        case 'XRP':
          minLength = 25;
          break;
        default:
          minLength = 10;
      }
      
      if (addressLength < minLength) {
        setErrors(prev => ({ 
          ...prev, 
          address: `${selectedCurrencySymbol} address should be at least ${minLength} characters long.` 
        }));
      }
    }
  };

  // Функция для получения баланса выбранного кошелька
  const getSelectedWalletBalance = () => {
    const selectedCurrency = currencies.find(c => c.symbol === selectedCurrencySymbol);
    if (!selectedCurrency) return 0;

    // Ищем кошелек по названию валюты или по символу
    const wallet = wallets.find(w => 
      w.asset_name?.toUpperCase() === selectedCurrency.name?.toUpperCase() ||
      w.asset_name?.toUpperCase() === selectedCurrencySymbol?.toUpperCase()
    );
    
   /* console.log('getSelectedWalletBalance:', {
      selectedCurrencySymbol,
      selectedCurrency,
      wallet,
      balance: wallet ? wallet.balance : 0
    });*/

    return wallet ? parseFloat(wallet.balance) || 0 : 0;
  };

  // Функция для получения адреса выбранного кошелька
  const getSelectedWalletAddress = () => {
    const selectedCurrency = currencies.find(c => c.symbol === selectedCurrencySymbol);
    if (!selectedCurrency) {
      //console.log("Selected currency not found:", selectedCurrencySymbol);
      return null;
    }

    // Ищем кошелек по названию валюты или по символу
    const wallet = wallets.find(w =>
      w.asset_name?.toUpperCase() === selectedCurrency.name?.toUpperCase() ||
      w.asset_name?.toUpperCase() === selectedCurrencySymbol?.toUpperCase()
    );

    if (!wallet) {
      //console.log("Wallet not found for currency:", selectedCurrency.name);
      //console.log("Available wallets:", wallets.map(w => ({ id: w.id, name: w.asset_name, address: w.address })));
      return null;
    }

    //console.log("Found wallet:", { id: wallet.id, name: wallet.asset_name, balance: wallet.balance, address: wallet.address });
    return wallet.address; // Возвращаем адрес вместо ID
  };

  // Функция для получения ID выбранного кошелька
  const getSelectedWalletId = () => {
    const selectedCurrency = currencies.find(c => c.symbol === selectedCurrencySymbol);
    if (!selectedCurrency) return null;

    // Ищем кошелек по названию валюты или по символу
    const wallet = wallets.find(w =>
      w.asset_name?.toUpperCase() === selectedCurrency.name?.toUpperCase() ||
      w.asset_name?.toUpperCase() === selectedCurrencySymbol?.toUpperCase()
    );

    return wallet ? wallet.id : null;
  };

  // Функция для получения ID актива
  const getSelectedAssetId = () => {
    const selectedCurrency = currencies.find(c => c.symbol === selectedCurrencySymbol);
    if (!selectedCurrency) return null;

    const wallet = wallets.find(w => w.asset_name === selectedCurrency.name);
    return wallet ? wallet.asset_id : null;
  };

  // Функция для валидации формы
  const validateForm = () => {
    const newErrors = {};
    const walletBalance = getSelectedWalletBalance();
    const amountValue = parseFloat(amount);
    const walletAddress = getSelectedWalletAddress(); // Теперь это адрес кошелька
    const walletId = getSelectedWalletId();
    const assetId = getSelectedAssetId();

    // Проверка выбора кошелька
    if (!walletAddress) {
      newErrors.wallet = "Please select a wallet.";
    }

    // Проверка ID кошелька
    if (!walletId) {
      newErrors.wallet = "Wallet ID not found. Please select a valid wallet.";
    }

    // Проверка ID актива
    if (!assetId) {
      newErrors.wallet = "Asset ID not found. Please select a valid wallet.";
    }

    // Проверка баланса
    if (walletBalance <= 0) {
      newErrors.balance = "Insufficient balance! Your wallet balance is 0.";
    } else if (amountValue > walletBalance) {
      newErrors.balance = `Insufficient balance! You have ${walletBalance} ${selectedCurrencySymbol}, but trying to send ${amount} ${selectedCurrencySymbol}.`;
    }

    // Проверка суммы
    if (!amount || amountValue <= 0) {
      newErrors.amount = "Please enter a valid amount.";
    } else {
      const minAmount = 0.0001;
      if (amountValue < minAmount) {
        newErrors.amount = `Minimum transaction amount is ${minAmount} ${selectedCurrencySymbol.toUpperCase()}.`;
      }
    }

    // Проверка адреса
    if (!sendAddress.trim()) {
      newErrors.address = "Please enter a valid address.";
    } else {
      const addressLength = sendAddress.trim().length;
      let minLength = 10; // Минимальная длина по умолчанию

      // Устанавливаем минимальную длину в зависимости от валюты
      switch (selectedCurrencySymbol) {
        case 'BTC':
          minLength = 26;
          break;
        case 'ETH':
          minLength = 42;
          break;
        case 'TRX':
          minLength = 34;
          break;
        case 'XRP':
          minLength = 25;
          break;
        default:
          minLength = 10;
      }

      if (addressLength < minLength) {
        newErrors.address = `${selectedCurrencySymbol} address should be at least ${minLength} characters long.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Функция для рендеринга
  const renderSelectedValue = (selectedSymbol) => {
    const walletData = buildDisplayWallet(selectedSymbol, wallets);
    if (!walletData) return null;
    return <CurrencyDisplay wallet={walletData} />;
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (regex.test(value)) {
      setAmount(value);
      // Очищаем ошибки суммы при вводе
      if (errors.amount || errors.balance) {
        setErrors(prev => ({ ...prev, amount: null, balance: null }));
      }
    }
  };

  // Функция для установки максимальной суммы
  const setMaxAmount = () => {
    const walletBalance = getSelectedWalletBalance();
    if (walletBalance > 0) {
      setAmount(walletBalance.toString());
      // Очищаем ошибки при установке максимальной суммы
      if (errors.amount || errors.balance) {
        setErrors(prev => ({ ...prev, amount: null, balance: null }));
      }
    }
  };

  const handlePasteAddress = async (e) => {
    e.preventDefault();
    try {
      const value = (await navigator.clipboard.readText()).trim();
      setSendAddress(value);
      sendAddressInput({ target: { value } });
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmationMethodChange = (event) => {
    setConfirmationMethod(event.target.value);
    setConfirmationCode("");
    setEmailCodeMessage("");
    setErrors((prev) => ({ ...prev, submit: null }));
  };

  const handleConfirmationCodeChange = (event) => {
    setConfirmationCode(event.target.value.replace(/\D/g, "").slice(0, 6));

    if (errors.submit) {
      setErrors((prev) => ({ ...prev, submit: null }));
    }
  };

  const requestEmailVerificationCode = async () => {
    if (emailCodeLoading || !securityStatus.email_2fa_enabled) return;
    if (!validateForm()) return;

    setEmailCodeLoading(true);
    setEmailCodeMessage("");
    setErrors((prev) => ({ ...prev, submit: null }));

    try {
      const response = await api.post("/email-authentication/code", {
        purpose: "withdrawal",
        amount,
        asset_ticker: selectedCurrencySymbol.toUpperCase(),
        to: sendAddress.trim(),
      });

      setConfirmationCode("");
      setEmailCodeMessage(response.data.message || "Verification code sent to your email.");
    } catch (err) {
      setErrors({
        submit: err.response?.data?.message || "Failed to send email verification code.",
      });
    } finally {
      setEmailCodeLoading(false);
    }
  };

  const sendTransaction = async () => {
    if (!validateForm() || isSubmitting) return;

    const hasConfirmationMethod =
      securityStatus.google_authenticator_enabled || securityStatus.email_2fa_enabled;

    if (securityStatus.loading) {
      setErrors({ submit: "Withdrawal confirmation settings are still loading." });
      return;
    }

    if (!hasConfirmationMethod) {
      setErrors({
        submit: "Set up Authenticator App or Email Authentication before sending funds.",
      });
      return;
    }

    if (!confirmationMethod || confirmationCode.length !== 6) {
      setErrors({ submit: "Enter the 6-digit confirmation code to continue." });
      return;
    }

    setIsSubmitting(true);
    const walletAddress = getSelectedWalletAddress(); // Теперь это адрес кошелька

    // Дополнительная проверка адреса кошелька
    if (!walletAddress) {
      setErrors({ submit: "Please select a wallet first." });
      setIsSubmitting(false);
      return;
    }

    const reqBody = {
      from: walletAddress, // Адрес кошелька отправителя
      to: sendAddress.trim(), // Адрес получателя
      amount: parseFloat(amount), // Сумма как число
      type: "send", // Тип транзакции
      wallet_id: getSelectedWalletId(), // ID кошелька
      asset_id: getSelectedAssetId(), // ID актива
    };

    reqBody.mfa_method = confirmationMethod;
    reqBody.mfa_code = confirmationCode;

    //console.log("Sending transaction with data:", reqBody);
    //console.log("Wallet address being sent:", walletAddress);
    //console.log("To address being sent:", sendAddress.trim());
    //console.log("Wallet ID being sent:", getSelectedWalletId());
    //console.log("Asset ID being sent:", getSelectedAssetId());
    //console.log("Selected currency:", selectedCurrencySymbol);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        setErrors({ submit: "Authentication required. Please log in again." });
        return;
      }

      await api.post("/transactions", reqBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      //console.log("Transaction response:", response.data);

      // Показываем успешное сообщение
      setErrors({ success: "Transaction sent successfully!" });

      // Очищаем форму
      setAmount("");
      setSendAddress("");
      setConfirmationCode("");
      setEmailCodeMessage("");

      // Редиректим
      setTimeout(() => {
        setErrors({});
        navigate("/dashboard/transactions");
      }, 1500);

    } catch (err) {
      console.error("Transaction error:", err.response?.data || err.message);

      // Обработка различных типов ошибок
      if (err.response?.status === 401) {
        setErrors({ submit: "Session expired. Please log in again." });
      } else if (err.response?.status === 422) {
        // Обработка ошибок валидации с сервера
        const serverErrors = err.response.data.errors || {};
        const errorMessages = Object.values(serverErrors).flat();
        setErrors({
          submit: errorMessages.length
            ? errorMessages.join(", ")
            : err.response.data.message || "Transaction confirmation failed.",
        });
      } else if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: "Error sending transaction. Please try again." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="send-panel">
      {/* Сообщения об ошибках и успехе */}
      {errors.success && (
        <div className="success-message">
          <Typography variant="body2" style={{ color: '#22c55e', marginBottom: '16px' }}>
            ✅ {errors.success}
          </Typography>
        </div>
      )}

      {errors.submit && (
        <div className="error-message">
          <Typography variant="body2" style={{ color: '#ef4444', marginBottom: '16px' }}>
            ❌ {errors.submit}
          </Typography>
        </div>
      )}

      <div className="send-amount-header">
        <TextField
          // fullWidth
          label={`${t('amount')} *`}
          variant="outlined"
          className="custom-textfield"
          placeholder="0.00"
          value={amount}
          onChange={handleAmountChange}
          error={!!errors.amount || !!errors.balance}
          helperText={errors.amount || errors.balance}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography className="currency-adornment-symbol">
                  {selectedCurrencySymbol.toUpperCase()}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        {/* Кнопка Max */}
        <Button
          variant="outlined"
          size="small"
          onClick={setMaxAmount}
          disabled={getSelectedWalletBalance() <= 0}
          style={{
            marginTop: '8px',
            fontSize: '0.7rem',
            padding: '4px 8px',
            minWidth: 'auto'
          }}
        >
          {t('max')}
        </Button>
        {/* Показываем текущий баланс */}
        <Typography
          variant="body2"
          style={{
            color: '#a0aec0',
            marginTop: '8px',
            fontSize: '0.8rem'
          }}
        >
          {t('available')}: {getSelectedWalletBalance().toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 })} {selectedCurrencySymbol.toUpperCase()}
        </Typography>
      </div>

      <TextField
        select
        fullWidth
        value={selectedCurrencySymbol}
        onChange={(e) => {
          setSelectedCurrencySymbol(e.target.value);
          // Очищаем ошибки при смене кошелька
          if (errors.amount || errors.balance || errors.wallet) {
            setErrors(prev => ({ ...prev, amount: null, balance: null, wallet: null }));
          }
        }}
        className="custom-select wallet-select-textfield"
        label={t('selectWallet')}
        error={!!errors.wallet}
        helperText={errors.wallet}
        SelectProps={{
          renderValue: renderSelectedValue,
        }}
      >
        {currencies.map((currency) => {
          const walletData = buildDisplayWallet(currency.symbol, wallets) || {
            ...currency,
            balance: 0,
            color: currency.color || "#ff9800",
          };

          return (
            <MenuItem key={currency.symbol} value={currency.symbol} className="wallet-select-item">
              <CurrencyDisplay wallet={walletData} />
            </MenuItem>
          );
        })}
      </TextField>

      <TextField
        fullWidth
        label={t('enterAddress')}
        variant="outlined"
        className="custom-textfield"
        onChange={sendAddressInput}
        value={sendAddress}
        error={!!errors.address}
        helperText={errors.address}
        InputProps={{
          endAdornment: (
            <Link href="#" className="paste-link" onClick={handlePasteAddress}>
              {t('paste')}
            </Link>
          ),
        }}
      />

      <div className="withdrawal-security-panel">
        <div className="withdrawal-security-header">
          <Typography className="withdrawal-security-title">
            Withdrawal confirmation
          </Typography>
          <Typography className="withdrawal-security-subtitle">
            Choose how to approve this withdrawal.
          </Typography>
        </div>

        {securityStatus.loading && (
          <div className="withdrawal-security-loading">
            <CircularProgress size={18} />
            <Typography>Loading confirmation methods...</Typography>
          </div>
        )}

        {!securityStatus.loading && securityStatusError && (
          <Typography className="withdrawal-security-error">
            {securityStatusError}
          </Typography>
        )}

        {!securityStatus.loading &&
          !securityStatusError &&
          !securityStatus.google_authenticator_enabled &&
          !securityStatus.email_2fa_enabled && (
            <Typography className="withdrawal-security-empty">
              To send funds, set up{" "}
              <Link
                component="button"
                type="button"
                className="withdrawal-security-link"
                onClick={() => navigate("/dashboard/account-settings?tab=security#multi-factor-authentication")}
              >
                Authenticator App or Email Authentication
              </Link>
              .
            </Typography>
          )}

        {!securityStatus.loading &&
          (securityStatus.google_authenticator_enabled || securityStatus.email_2fa_enabled) && (
            <>
              <TextField
                select
                fullWidth
                label="Confirmation method"
                className="custom-textfield withdrawal-method-select"
                value={confirmationMethod}
                onChange={handleConfirmationMethodChange}
              >
                {securityStatus.google_authenticator_enabled && (
                  <MenuItem value="authenticator">Authenticator App</MenuItem>
                )}
                {securityStatus.email_2fa_enabled && (
                  <MenuItem value="email">Email Authentication</MenuItem>
                )}
              </TextField>

              {confirmationMethod === "email" && (
                <div className="withdrawal-email-code-row">
                  <Button
                    variant="outlined"
                    className="withdrawal-code-button"
                    onClick={requestEmailVerificationCode}
                    disabled={emailCodeLoading}
                    startIcon={emailCodeLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {emailCodeLoading ? "Sending..." : "Send code"}
                  </Button>
                  {emailCodeMessage && (
                    <Typography className="withdrawal-security-success">
                      {emailCodeMessage}
                    </Typography>
                  )}
                </div>
              )}

              <TextField
                fullWidth
                label={confirmationMethod === "email" ? "Email verification code" : "Authenticator code"}
                variant="outlined"
                className="custom-textfield"
                value={confirmationCode}
                onChange={handleConfirmationCodeChange}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
              />
            </>
          )}
      </div>

      <Button
        fullWidth
        variant="contained"
        className="action-button"
        onClick={sendTransaction}
        disabled={
          isSubmitting ||
          securityStatus.loading ||
          !confirmationMethod ||
          confirmationCode.length !== 6
        }
        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
      >
        {isSubmitting ? t('sending') : t('send')}
      </Button>
    </div>
  );
}

function ReceivePanel() {
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState(
    currencies[0]?.symbol || ""
  );
  const { t } = useLanguage();
  const [walletAddress, setWalletAddress] = useState("System is generating...");
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const { wallets } = useApp();

  // Функция для получения адреса кошелька
  const getWalletAddress = async (symbol) => {
    setIsLoadingAddress(true);
    try {
      // Находим кошелек по символу валюты
      const wallet = wallets.find(w => {
        const currency = currencies.find(c => c.symbol === symbol);
        return currency && w.asset_name === currency.name;
      });

      if (wallet && wallet.address && wallet.address !== "System is generating...") {
        setWalletAddress(wallet.address);
      } else {
        setWalletAddress("System is generating...");
      }
    } catch (error) {
      //console.log('Error getting wallet address:', error);
      setWalletAddress("System is generating...");
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Обновляем адрес при изменении выбранной валюты
  useEffect(() => {
    getWalletAddress(selectedCurrencySymbol);
  }, [selectedCurrencySymbol, wallets]);

  // Функция для копирования адреса
  const copyAddress = async () => {
    if (walletAddress && walletAddress !== "System is generating...") {
      try {
        await navigator.clipboard.writeText(walletAddress);
        //console.log('Address copied to clipboard');
        // Можно добавить toast уведомление здесь
        alert('Address copied to clipboard!');
      } catch (err) {
        //console.log('Failed to copy address:', err);
        alert('Failed to copy address');
      }
    }
  };

  // Функция для рендеринга
  const renderSelectedValue = (selectedSymbol) => {
    const walletData = buildDisplayWallet(selectedSymbol, wallets);
    if (!walletData) return null;
    return <CurrencyDisplay wallet={walletData} />;
  };

  // Проверяем, есть ли реальный адрес
  const hasRealAddress = walletAddress && walletAddress !== "System is generating...";

  return (
    <div className="receive-panel">
      {/* QR-код - показываем только если есть реальный адрес */}
      {hasRealAddress && (
        <div className="qr-code-container">
          <div className="qr-code-border">
            <img 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(walletAddress)}`}
              alt="QR Code"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>
        </div>
      )}

      {/* Селектор кошелька */}
      <TextField
        select
        fullWidth
        value={selectedCurrencySymbol}
        onChange={(e) => setSelectedCurrencySymbol(e.target.value)}
        className="custom-select wallet-select-textfield"
        label={t('selectWallet')}
        SelectProps={{
          renderValue: renderSelectedValue,
        }}
      >
        {currencies.map((currency) => {
          const walletData = buildDisplayWallet(currency.symbol, wallets) || {
            ...currency,
            balance: 0,
            color: currency.color || "#ff9800",
          };

          return (
            <MenuItem key={currency.symbol} value={currency.symbol} className="wallet-select-item">
              <CurrencyDisplay wallet={walletData} />
            </MenuItem>
          );
        })}
      </TextField>

      {/* Поле с адресом */}
      <div className="address-display-box">
        <Typography className="generating-text">
          {isLoadingAddress ? t('loading') : (
            hasRealAddress ? (
              <span style={{ color: 'white' }}>
                {walletAddress}
              </span>
            ) : (
              walletAddress
            )
          )}
        </Typography>
        <IconButton 
          className="copy-icon-button"
          onClick={copyAddress}
          disabled={!hasRealAddress}
          title={hasRealAddress ? t('copyAddress') : t('generatingAddress')}
        >
          <ContentCopyIcon />
        </IconButton>
      </div>
    </div>
  );
}

function SendReceiveForm() {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useLanguage();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Paper elevation={0} className="card send-receive-form">
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className="custom-tabs"
        variant="fullWidth"
      >
        <Tab label={t('send')} className="custom-tab" />
        <Tab label={t('receiving')} className="custom-tab" />
      </Tabs>

      <div className="form-panel">
        {activeTab === 0 && (
          // Панель "Send"
          <SendPanel />
        )}
        {activeTab === 1 && (
          // Панель "Receive" (заглушка)
          <ReceivePanel />
        )}
      </div>
    </Paper>
  );
}

export default function WalletsPage() {
  const { t } = useLanguage();
  return (
    <div className="wallets-page-layout">
      <div className="wallets-left-column">
        <YourAssetsCard />
        {/* <PlaceholderCard
            title="Recent Transactions"
            message="We couldn't find any transactions."
        /> */}
      </div>

      <div className="wallets-right-column">
        <SendReceiveForm />
      </div>
    </div>
  );
}
